import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tent, Users, AlertCircle, Search, GraduationCap } from 'lucide-react';
import { reviewerRoles } from '../lib/utils';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'facilitator',
    reviewer_role: '',
    popia_consent: false,
    background_check_consent: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef(null);
  const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    // Validate facilitator consents
    if (formData.role === 'facilitator') {
      if (!formData.popia_consent) {
        setError('You must consent to POPIA to register as a facilitator');
        return;
      }
      if (!formData.background_check_consent) {
        setError('You must consent to background checks to register as a facilitator');
        return;
      }
    }

    // Validate reviewer role
    if (formData.role === 'reviewer' && !formData.reviewer_role) {
      setError('Please select your role (Parent, Teacher, etc.)');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the "I am human" check below');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        reviewer_role: formData.role === 'reviewer' ? formData.reviewer_role : null,
        popia_consent: formData.popia_consent,
        background_check_consent: formData.background_check_consent,
        hcaptcha_token: captchaToken,
      });
      
      // Redirect reviewer accounts to browse directory, others to dashboard
      if (formData.role === 'reviewer') {
        navigate('/directory/camps');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
      // hCaptcha tokens are single-use — reset after any failed attempt
      setCaptchaToken('');
      captchaRef.current?.resetCaptcha();
    }
    setLoading(false);
  };

  const getNameLabel = () => {
    switch (formData.role) {
      case 'facilitator':
        return 'Full Name';
      case 'camp':
        return 'Camp / Organisation Name';
      case 'reviewer':
        return 'Your Name';
      default:
        return 'Name';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 pb-20 md:pb-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Tent className="w-7 h-7 text-primary-foreground" />
            </div>
          </Link>
          <h1 className="font-heading font-bold text-2xl mt-4">Get Listed</h1>
          <p className="text-muted-foreground">Join Campia&apos;s trusted camp directory</p>
        </div>

        {/* Form */}
        <div className="bg-card rounded-2xl border border-border p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl text-sm" data-testid="register-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Role Selection */}
            <div className="space-y-3">
              <Label>I am a...</Label>
              <RadioGroup
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value, reviewer_role: '' })}
                className="grid grid-cols-1 gap-3"
              >
                <div>
                  <RadioGroupItem
                    value="facilitator"
                    id="facilitator"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="facilitator"
                    className="flex items-center gap-3 p-4 border-2 border-border rounded-xl cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 hover:bg-muted/50 transition-all"
                    data-testid="role-facilitator"
                  >
                    <Users className="w-6 h-6 text-primary" />
                    <div>
                      <span className="font-medium">Facilitator</span>
                      <p className="text-xs text-muted-foreground">I lead or facilitate camps</p>
                    </div>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="camp"
                    id="camp"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="camp"
                    className="flex items-center gap-3 p-4 border-2 border-border rounded-xl cursor-pointer peer-data-[state=checked]:border-secondary peer-data-[state=checked]:bg-secondary/5 hover:bg-muted/50 transition-all"
                    data-testid="role-camp"
                  >
                    <Tent className="w-6 h-6 text-secondary" />
                    <div>
                      <span className="font-medium">Camp / Venue</span>
                      <p className="text-xs text-muted-foreground">I own or manage a camp venue</p>
                    </div>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="reviewer"
                    id="reviewer"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="reviewer"
                    className="flex items-center gap-3 p-4 border-2 border-border rounded-xl cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 hover:bg-muted/50 transition-all"
                    data-testid="role-reviewer"
                  >
                    <GraduationCap className="w-6 h-6 text-primary" />
                    <div>
                      <span className="font-medium">School or Parent</span>
                      <p className="text-xs text-muted-foreground">I want to browse and review camps</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Reviewer Role Selection */}
            {formData.role === 'reviewer' && (
              <div className="space-y-2">
                <Label>Your Role</Label>
                <Select
                  value={formData.reviewer_role}
                  onValueChange={(value) => setFormData({ ...formData, reviewer_role: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="reviewer-role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {reviewerRoles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">{getNameLabel()}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                className="h-12 rounded-xl"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 rounded-xl"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                className="h-12 rounded-xl"
                data-testid="register-password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-12 rounded-xl"
                data-testid="register-confirm-password"
              />
            </div>

            {/* Facilitator Consent Checkboxes */}
            {formData.role === 'facilitator' && (
              <div className="space-y-4 pt-2 border-t border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="popia_consent"
                    checked={formData.popia_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, popia_consent: checked })}
                    className="mt-1"
                    data-testid="popia-consent"
                  />
                  <Label htmlFor="popia_consent" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                    I consent to Campia collecting, storing, and processing my personal information in accordance with the Protection of Personal Information Act (POPIA) and Campia&apos;s{' '}
                    <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>.
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="background_check_consent"
                    checked={formData.background_check_consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, background_check_consent: checked })}
                    className="mt-1"
                    data-testid="background-consent"
                  />
                  <Label htmlFor="background_check_consent" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                    I consent to Campia conducting or requesting background and safeguarding checks as part of the verification process. I understand that my profile verification status depends on this.
                  </Label>
                </div>
              </div>
            )}

            <div className="flex justify-center" data-testid="register-captcha">
              <HCaptcha
                ref={captchaRef}
                sitekey={HCAPTCHA_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken('')}
                onError={() => setCaptchaToken('')}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-full"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        {/* Browse without account notice */}
        <div className="bg-muted/50 rounded-xl p-4 mt-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Just want to browse? No account needed.
          </p>
          <Link to="/directory/camps">
            <Button variant="outline" size="sm" className="rounded-full">
              <Search className="w-4 h-4 mr-2" />
              Browse Directory
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
